import React, { useState, useMemo } from 'react';
import DictationTextArea from '../../../../components/DictationTextArea';
import InfoTooltip from '../../../../components/InfoTooltip';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import { SearchIcon, ClockIcon } from '@heroicons/react/solid';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../../utils/formDisplayUtils';
import { refreshDobAge } from '../../../serviceUsers/3_forms/functions/PrefillFunctions';

export default function CIQuestionPanel({
  currentSectionNumber,
  currentSectionTitle,
  panel,
  panelIndex,
  notes,
  handleNotesChange,
  handleNotesBlur,
  patientFormData,
  canEdit
}) {

  // The stored prefill is shown as written, with one exception: the date of
  // birth carries an age that was calculated when the patient submitted the
  // form, so it is recalculated for today at render time (see refreshDobAge).
  // Only the dateOfBirth row is touched; every other value passes through.
  const patientPrefill = useMemo(
    () => Object.entries(panel.subsectionQuestions).map(([key, q]) => {
      const stored = q.patientPrefill || '';
      return q.providerVarName === 'dateOfBirth' ? refreshDobAge(stored) : stored;
    }),
    [panel.subsectionQuestions]
  );

  // State to track which question's dialog is open
  const [openDialogIndex, setOpenDialogIndex] = useState(null);

  // State to store the raw prefill data for the current dialog
  const [rawPrefillData, setRawPrefillData] = useState([]);
  // The patientPrefill string for the currently open dialog question,
  // used as a fallback displayText when the raw form data doesn't contain
  // free text (e.g. demo patients with legacy otherAnswer not in Firestore)
  const [dialogPrefillText, setDialogPrefillText] = useState('');

  const handleLocalNotesBlur = () => {
    if (!canEdit) return;
    handleNotesBlur(panelIndex);
  };

  // Look up the raw answer objects for each question's prefill source so
  // we can render italic curly-quoted free text via FormattedAnswer.
  //
  // Only resolves for single-source-variable questions (varNames.length === 1),
  // where there's a direct 1:1 mapping between the CI question and the form
  // subquestion's raw answer.
  //
  // Multi-varName questions return null intentionally: their patientPrefill
  // string is assembled by PrefillFunctions.js with rich contextual formatting
  // (question titles, grouping, concatenation logic like "Name" = title + first
  // + middle + last). Substituting raw answer objects would lose all of that
  // context. For multi-varName questions, italic curly-quoted otherValue is
  // still visible in the magnifying glass dialog, which does its own per-
  // subquestion raw answer lookup via getRawPrefillInputs.
  const rawPrefillAnswers = useMemo(() => {
    if (!patientFormData) return [];

    return Object.values(panel.subsectionQuestions).map((question) => {
      const varNames = question.prefilledByServiceUserVarNames;
      if (!varNames || varNames.length !== 1) return null;

      const varName = varNames[0];

      for (const formValue of Object.values(patientFormData)) {
        for (const subsection of Object.values(formValue.formSubsections || {})) {
          for (const formQuestion of Object.values(subsection.questions || {})) {
            for (const [subKey, subquestion] of Object.entries(formQuestion.subquestions || {})) {
              if (isNaN(parseInt(subKey))) continue;
              if (subquestion.serviceUserVarName === varName) {
                return {
                  rawAnswer: normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer),
                  inputType: subquestion.inputType,
                };
              }
            }
          }
        }
      }

      return null;
    });
  }, [panel.subsectionQuestions, patientFormData]);

  /// Function to fetch raw prefill data when dialog opens
  const handleOpenRawPrefillDialog = (index) => {
    const question = Object.values(panel.subsectionQuestions)[index];
    
    // Only open dialog if we know which variables generated it
    if (question.prefilledByServiceUserVarNames?.length > 0) {
      const inputs = getRawPrefillInputs(question.prefilledByServiceUserVarNames);
      setRawPrefillData(inputs);
      setDialogPrefillText(patientPrefill[index] || '');
      setOpenDialogIndex(index);
    }
  };

// Function to extract raw prefill inputs from formData
// Note: For patients with workflowIncludesPhysicalHealthQuestions = false,
// physical health data won't exist in their formData. This function will
// return an empty array for those questions, and the dialog will show
// "No source data available" - which is the correct behavior.
const getRawPrefillInputs = (varNames) => {
  if (!patientFormData || !varNames || varNames.length === 0) {
    return [];
  }
  
  const results = [];
  
  // Search through all form data to find matching variable names
  Object.entries(patientFormData).forEach(([formKey, formValue]) => {
    Object.entries(formValue.formSubsections || {}).forEach(([subsectionKey, subsection]) => {
      Object.entries(subsection.questions || {}).forEach(([questionKey, question]) => {
        // Check each question for matching variable names
        const subquestions = question.subquestions || {};
        
        Object.entries(subquestions).forEach(([subquestionKey, subquestion]) => {
          // Skip non-numbered subquestion keys like 'noneOptions'
          if (isNaN(parseInt(subquestionKey))) return;
          
          // Include questions that match the variable names
          if (varNames.includes(subquestion.serviceUserVarName)) {
            const normalized = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
            const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];
            
            results.push({
              formName: formValue.formTitle || formKey,
              questionTitle: question.questionTitle,
              question: subquestion.subquestionDisplayText,
              answer: buildDisplayAnswer(normalized),
              hasAnswer: normalized !== null && normalized !== undefined && normalized !== '',
              inputType: subquestion.inputType || '',
              options: allOptions,
              rawAnswer: normalized,
              wasSkipped: question.wasSkipped,
              skipButtonText: question.skipButtonText
            });
          }
        });
      });
    });
  });
  
  return results;
};

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
      <div className="flex items-center">
        <h4 className="font-bold mb-2 text-lg w-80">{panel.subsectionDisplayTitle}</h4>
        <span className={`text-sm w-1/2 text-gray-500 italic ml-5`}>
          Patient Self-report
        </span>
        {panel.hasInformantPrefill && (
          <span className="text-sm w-1/2 text-gray-500 italic ml-5">Informant Report</span>
        )}
      </div>
      <div className="border-t border-gray-300 pt-4">
        {Object.entries(panel.subsectionQuestions).map(([key, question], index) => (
          <div key={key} className="flex items-start mb-2">
            <div className="w-80 flex items-center gap-2">
              {question.info ? (
                <InfoTooltip 
                  content={question.info} 
                  label={`Information about ${question.title}`}
                  maxWidth="300px"
                  className=""
                />
              ) : (
                <div className="w-4 h-4"></div>
              )}
              <span className="font-semibold">{question.title}</span>
            </div>

            {/* Read-only prefill display — uses a div instead of textarea so we can
                render italic curly-quoted free text via FormattedAnswer. Visually
                matches the disabled AutoResizingTextArea (same width, padding,
                border, background, and text colour). */}
            <div className="w-1/2 ml-5 px-3 py-2 border rounded-md bg-gray-100 text-gray-700 whitespace-pre-wrap">
              {patientPrefill[index] ? (
                <FormattedAnswer
                  rawAnswer={rawPrefillAnswers[index]?.rawAnswer}
                  displayText={patientPrefill[index]}
                  inputType={rawPrefillAnswers[index]?.inputType}
                />
              ) : (
                <span className="text-gray-400">No answer given</span>
              )}
            </div>

            {/* Wrap in Dialog */}
            <Dialog.Root open={openDialogIndex === index} onOpenChange={(open) => !open && setOpenDialogIndex(null)}>
  <Dialog.Trigger asChild>
    <div
      className="cursor-pointer ml-2 mt-2"
      onClick={() => handleOpenRawPrefillDialog(index)}
    >
      <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
        <SearchIcon className="h-3 w-3" />
        
      </div>
    </div>
  </Dialog.Trigger>

              <Dialog.Portal>
                <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
  <div className="flex justify-between items-center mb-4">
    <Dialog.Title className="text-base font-semibold text-gray-900">
      Patient Form Responses
    </Dialog.Title>
    <Dialog.Close asChild>
      <button className="p-1 rounded-full hover:bg-gray-100">
        <XIcon className="h-5 w-5 text-gray-500" />
      </button>
    </Dialog.Close>
  </div>

  <div className="mb-4">
    <h3 className="font-semibold text-sm mb-2">{question?.title}</h3>
    
    {/* Last updated timestamp */}
    {question.patientPrefillLastUpdatedAt && (
      <div className="text-xs text-gray-500 mb-3 flex items-center">
        <ClockIcon className="h-3 w-3 mr-1" /> 
        Last updated: {formatDateTime(question.patientPrefillLastUpdatedAt)}
      </div>
    )}
                    <div className="space-y-2 mt-2">
                    {rawPrefillData.length > 0 ? (
  rawPrefillData.map((item, i) => (
    <div key={i} className={`border rounded-md p-3 ${
      item.hasAnswer 
        ? 'bg-violet-50 shadow-sm border-violet-200' 
        : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
    }`}>
      <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>
      
      {/* Display question title if available */}
      {item.questionTitle && (
        <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
      )}
      
      <div className="text-xs font-medium mb-1.5">{item.question}</div>

      {/* Display skip badge if question was skipped */}
      {item.wasSkipped && (
        <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
          <span>Patient clicked</span>
          <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
            {item.skipButtonText}
          </span>
        </div>
      )}

      {/* Display the answer — FormattedAnswer renders any otherValue free text
          inline in italic curly quotes so clinicians see it at a glance.
          For single-item dialogs, pass the patientPrefill string as displayText
          so FormattedAnswer can recover free text that may be missing from the
          raw Firestore data (e.g. demo patients with legacy otherAnswer). */}
      <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${
        item.hasAnswer 
          ? 'bg-white border-gray-200' 
          : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
      }`}>
        <FormattedAnswer
          rawAnswer={item.rawAnswer}
          displayText={rawPrefillData.length === 1 && dialogPrefillText ? dialogPrefillText : item.answer}
          inputType={item.inputType}
        />
      </div>

      {/* Display available options for selection questions */}
      {item.options && item.options.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-1">Available options:</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {item.options.map((option, optIndex) => {
              const isSelected = item.hasAnswer && (() => {
                // For simple string match
                if (typeof item.rawAnswer === 'string') {
                  return item.rawAnswer === option;
                }
                
                // For array of strings
                if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'string') {
                  return item.rawAnswer.includes(option);
                }
                
                // For array of objects with value property
                if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'object') {
                  return item.rawAnswer.some(ans => ans.value === option);
                }
                
                // For single object with value property
                if (item.rawAnswer && typeof item.rawAnswer === 'object' && 'value' in item.rawAnswer) {
                  return item.rawAnswer.value === option;
                }
                
                return false;
              })();

              return (
                <div
                  key={optIndex}
                  className={`text-xs p-1.5 rounded ${
                    isSelected
                      ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                      : 'bg-gray-50 border border-gray-200 text-gray-500'
                  }`}
                >
                  {option}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  ))
) : (
  <p className="text-gray-500 italic">No source data available</p>
)}
                    </div>
                  </div>                  
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

          </div>
        ))
        }
        {/* Patient Information has no AI-generated paragraph (no backend
            prompt config or SECTION_PARAGRAPHS entry), so clinician notes
            typed here would never reach the report. Hide the notes box on
            that section, matching the CYP ADHD CI pattern. */}
        {currentSectionTitle !== 'Patient Information' && (
          <div className="mt-4">
            <h4 className="font-semibold mb-2 text-lg">Clinician Notes</h4>
            <DictationTextArea
              placeholder={canEdit ? "Add general comments or observations here" : "Notes can only be added by the assigned clinician"}
              value={notes[panelIndex] ?? panel.clinicianNotes ?? ''}
              onChange={(val) => handleNotesChange(panelIndex, val)}
              onBlur={handleLocalNotesBlur}
              canEdit={canEdit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
